import React, { useMemo } from 'react';
import TickIcon from '@mui/icons-material/Done';
import { Box } from '@mui/material';
import { trpc } from '@client-trpc';
import {
  GridActionConfig,
  ManageGrid,
  ManageGridColumn
} from '@client/foundation/shared/components/manage-grid';

export type WorkspaceLayoutsGridProps = {
  activeLayoutId?: string;
  onApply: (layoutId: string) => void;
  onDelete: (layoutIds: string[]) => void;
  onModify: (
    layoutId: string,
    layoutName: string,
    layoutNotes?: string
  ) => void;
  onSetDefault: (layoutId: string, isDefault: boolean) => void;
  onShare?: (layoutId: string, layoutName: string) => void;
};

type WorkspaceLayoutRow = {
  id: string;
  name: string;
  notes?: string;
  isDefault: boolean;
  isActive: boolean;
};

const columns: ManageGridColumn<WorkspaceLayoutRow>[] = [
  {
    key: 'name',
    header: 'Name',
    renderCell: (row) => row.name
  },
  {
    key: 'default',
    header: 'Default',
    renderCell: (row) =>
      row.isDefault ? <TickIcon color="success" fontSize="small" /> : null
  },
  {
    key: 'active',
    header: 'Active',
    renderCell: (row) =>
      row.isActive ? <TickIcon color="success" fontSize="small" /> : null
  },
  {
    key: 'notes',
    header: 'Notes',
    renderCell: (row) =>
      row.notes ? (
        <Box
          sx={{
            maxWidth: 360,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere'
          }}
        >
          {row.notes}
        </Box>
      ) : null
  }
];

export const WorkspaceLayoutsGrid: React.FC<WorkspaceLayoutsGridProps> = ({
  activeLayoutId,
  onApply,
  onDelete,
  onModify,
  onSetDefault,
  onShare
}) => {
  const { data: rows = [] } = trpc.workspaceLayouts.workspaceLayouts.useQuery(
    undefined,
    {
      select: (layouts) =>
        layouts.map(({ id, name, notes, isDefault }) => ({
          id,
          name,
          notes,
          isDefault,
          isActive: id === activeLayoutId
        }))
    }
  );
  const actions = useMemo<GridActionConfig<WorkspaceLayoutRow>[]>(() => {
    const next: GridActionConfig<WorkspaceLayoutRow>[] = [
      {
        key: 'clear',
        label: 'Clear',
        tone: 'secondary',
        selectionMode: 'multi',
        onClick: ({ clearSelection }) => clearSelection()
      },
      {
        key: 'delete',
        label: 'Delete',
        tone: 'danger',
        selectionMode: 'multi',
        onClick: ({ selectedIds, clearSelection }) => {
          onDelete(selectedIds);
          clearSelection();
        }
      },
      {
        key: 'toggle-default',
        label: 'Toggle default',
        selectionMode: 'single',
        onClick: ({ selectedRow }) => {
          if (!selectedRow) return;
          onSetDefault(selectedRow.id, selectedRow.isDefault);
        }
      },
      {
        key: 'modify',
        label: 'Modify',
        selectionMode: 'single',
        onClick: ({ selectedRow }) => {
          if (!selectedRow) return;
          onModify(selectedRow.id, selectedRow.name, selectedRow.notes);
        }
      }
    ];

    if (onShare) {
      next.push({
        key: 'share',
        label: 'Share',
        selectionMode: 'single',
        onClick: ({ selectedRow }) => {
          if (!selectedRow) return;
          onShare(selectedRow.id, selectedRow.name);
        }
      });
    }

    next.push({
      key: 'apply',
      label: 'Apply',
      selectionMode: 'single',
      onClick: ({ selectedRow }) => {
        if (!selectedRow) return;
        onApply(selectedRow.id);
      }
    });

    return next;
  }, [onApply, onDelete, onModify, onSetDefault, onShare]);

  return (
    <ManageGrid
      rows={rows}
      columns={columns}
      actions={actions}
    />
  );
};
